/**
 * The assistant's face in React, the same for every place that shows one (the scene behind the
 * regions, the avatar module): which face shows (useFaceKind), what it is doing (useFaceState), the
 * last emoji cue (useFaceCue), the platform's own faces (IdleFace, ParticlesFace), and an outside
 * face with its stand-in until it is live (LiveFace).
 */
import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import type { Box } from '../layout/geometry';
import { isThinking } from '../core/conversation';
import { useApp } from '../react/app';
import { useAppearance, useConversation, useStage, useTheme } from '../react/hooks';
import { useStore } from '../react/useStore';
import { useVoice } from '../voice/useVoice';
import { faceStateOf, facesHere, resolveFace, type FaceKind, type FaceState } from './face';
import { KeyedVideo } from './KeyedVideo';
import { SpeakingIdle } from './SpeakingIdle';
import type { FaceCue } from './cues';
import { ParticlesOrb } from './particles/ParticlesOrb';

/**
 * The face that shows where `asked` is wanted: Settings' choice first, else `asked` if this screen has
 * it, else the theme's stand-in (Appearance.faceFallback). Also the stand-in, for while MuseTalk connects.
 */
export function useFaceKind(asked: FaceKind): { kind: FaceKind; standIn: FaceKind } {
  const { avatar } = useApp();
  const stage = useStage();
  const { face, faceFallback } = useAppearance();
  const has = facesHere(avatar, stage.avatar !== null);
  return { kind: resolveFace(face, asked, has, [faceFallback]), standIn: has(faceFallback) ? faceFallback : 'particles' };
}

/**
 * The stage's avatar idle (breathing, blinking), its green screen keyed out, filling its box like the live video
 * would. The first frame shows until the loop plays, and alone when animations are off. An avatar with recorded
 * mouths (`mouths`) also speaks the replies, where there is a voice (SpeakingIdle).
 */
export function IdleFace() {
  const { motion } = useAppearance();
  const { avatar } = useStage();
  const { voice } = useApp();
  if (!avatar) return null;
  const video = <KeyedVideo video={avatar.idle.video} poster={avatar.idle.poster} keying={avatar.key} still={!motion} />;
  if (!avatar.mouths || !motion || !voice) return video;
  return <SpeakingIdle plateId={avatar.mouths} keying={avatar.key} bridge={voice.avatar} fallback={video} />;
}

export function useFaceState(): FaceState {
  const { voice } = useApp();
  const voiceState = useVoice((v) => v);
  const thinking = useConversation(isThinking);
  return faceStateOf(voice ? voiceState : null, thinking);
}

export function useFaceCue(): FaceCue | null {
  return useStore(useApp().cues, (c) => c);
}

/**
 * The particle orb in its box. The sphere fills about two thirds of its canvas, so by default the
 * canvas is larger than the box (`scale`) and overflows it, centred: the sphere fills the box.
 */
export function ParticlesFace({ scale = 1.4, style }: { scale?: number; style?: CSSProperties }) {
  const host = useRef<HTMLDivElement>(null);
  const [side, setSide] = useState(0);
  const state = useFaceState();
  const cue = useFaceCue();
  const theme = useTheme();
  const { scheme } = useAppearance();

  useLayoutEffect(() => {
    const el = host.current;
    if (!el) return;
    const measure = () => setSide(Math.floor(Math.min(el.clientWidth, el.clientHeight) * scale));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [scale]);

  return (
    <div ref={host} style={{ position: 'relative', width: '100%', height: '100%', ...style }} data-face="particles">
      {side > 0 && (
        <div style={{ position: 'absolute', left: '50%', top: '50%', transform: 'translate(-50%, -50%)' }}>
          <ParticlesOrb state={state} size={side} paletteKey={`${theme.id}:${scheme}`} look={scheme === 'dark' ? 'glow' : 'solid'} cue={cue} label="assistant" />
        </div>
      )}
    </div>
  );
}

/**
 * An outside face (MuseTalk, three.js) and what shows until it is live: it stays mounted under the
 * stand-in, so it connects and takes over at its first frame — and gives way again if it drops.
 */
export function LiveFace({ kind, box, standIn, frame }: {
  kind: FaceKind;
  box: Box;
  standIn: ReactNode;
  /** Wraps the outside face (the avatar module's circle); the stand-in shows without it. */
  frame?: (face: ReactNode) => ReactNode;
}) {
  const { avatar } = useApp();
  const [live, setLive] = useState(false);
  if (!avatar) return <>{standIn}</>;
  const face = <avatar.Render box={box} kind={kind} onLive={setLive} />;
  return (
    <div style={{ position: 'relative', width: '100%', height: '100%' }} data-live={live}>
      <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: live ? 1 : 0, transition: 'opacity 400ms ease' }}>
        {frame ? frame(face) : face}
      </div>
      {!live && <div style={{ position: 'absolute', inset: 0 }}>{standIn}</div>}
    </div>
  );
}
